// HyperionToolTip.jsx
//
// App-wide tooltip. Two variants:
//   "dark"  — for use on dark surfaces (dynamic island, panels, dark cards)
//   "light" — for use on light surfaces (MUI datagrid, white backgrounds)
//
// Usage:
//   <HyperionToolTip title="Some label" variant="dark">
//     <span>...</span>
//   </HyperionToolTip>
//
//   <HyperionToolTip title="Some label" variant="light" placement="bottom">
//     <button>...</button>
//   </HyperionToolTip>
//
//   <HyperionToolTip rich arrow={false} open={open} variant="dark" title={<MyWidget />}>
//     <span>trigger</span>
//   </HyperionToolTip>
//
// All standard MUI Tooltip props are forwarded (placement, disableHoverListener, etc.)

import React from "react";
import Tooltip from "@mui/material/Tooltip";
import "./HyperionToolTip.css";

/**
 * HyperionToolTip
 *
 * @param {"dark"|"light"} [variant="dark"]   — surface context
 * @param {boolean}        [arrow=true]        — show directional arrow
 * @param {number}         [delay=300]         — open delay in ms
 * @param {number}         [closeDelay=100]    — close delay in ms
 * @param {boolean}        [rich=false]        — strip default chrome so the title
 *                                               content handles its own styling;
 *                                               intended for interactive/complex content
 *                                               such as sliders, menus, or custom panels.
 *
 * All other MUI Tooltip props (title, placement, disableHoverListener…) pass through.
 */
const HyperionToolTip = ({
  variant = "dark",
  arrow = true,
  delay = 300,
  closeDelay = 100,
  rich = false,
  children,
  ...props
}) => (
  <Tooltip
    arrow={arrow}
    enterDelay={delay}
    enterNextDelay={100}
    leaveDelay={closeDelay}
    classes={{
      tooltip: ["ht-tooltip", `ht-tooltip--${variant}`, rich ? "ht-tooltip--rich" : ""]
                 .filter(Boolean).join(" "),
      arrow:   `ht-arrow ht-arrow--${variant}`,
    }}
    {...props}
  >
    {/* MUI Tooltip needs a single child with a real layout box to anchor to.
        inline-flex sizes to content and has no visual impact on the badges. */}
    <span style={{ display: "inline-flex", alignItems: "center" }}>{children}</span>
  </Tooltip>
);

export default HyperionToolTip;